import { useState } from 'react'
import type { MediaEntry } from '../api/schema.js'
import { invitation } from '../data/invitation.js'
import { galleryTileSpan } from '../lib/framing.js'
import styles from './Gallery.module.css'
import { Lightbox } from './Lightbox.js'

export interface GalleryProps {
  entries: MediaEntry[]
}

export function Gallery({ entries }: GalleryProps) {
  const total = entries.length
  const [visible, setVisible] = useState<number>(invitation.gallery.initialCount)
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  // 디코딩하지 못하는 사진(예: 안드로이드 크롬의 HEIC)은 액자만 남기고 물러난다 —
  // 타일 자체는 남으므로 격자가 무너지지 않는다.
  const [broken, setBroken] = useState<ReadonlySet<string>>(() => new Set())
  // URL 기준으로 보관해야 같은 gallery 번호의 사진이 교체돼도 옛 방향이 남지 않는다.
  const [naturalSizes, setNaturalSizes] = useState<ReadonlyMap<string, { width: number; height: number }>>(
    () => new Map(),
  )

  if (total === 0) return null

  const shown = Math.min(visible, total)
  const allShown = shown >= total
  const showToggle = total > invitation.gallery.initialCount

  function handleToggle() {
    setVisible((current) =>
      current >= total ? invitation.gallery.initialCount : total,
    )
  }

  return (
    <div className={styles.wrap}>
      <h2 className={styles.title}>갤러리</h2>
      <div className={styles.subtitle}>사진을 누르면 크게 볼 수 있어요</div>
      <div className={styles.grid}>
        {entries.slice(0, shown).map((entry, index) => {
          const { span, aspect } = galleryTileSpan(naturalSizes.get(entry.url) ?? entry)
          return (
            <button
              key={entry.key}
              type="button"
              className={styles.tile}
              style={{ gridColumn: span }}
              aria-label={`${index + 1}번 웨딩 사진 크게 보기`}
              onClick={() => setOpenIndex(index)}
            >
              <span className={styles.frame} style={{ aspectRatio: aspect }}>
                {!broken.has(entry.key) && (
                  <img
                    className={styles.photo}
                    src={entry.url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onLoad={(event) => {
                      const { naturalWidth: width, naturalHeight: height } = event.currentTarget
                      if (width <= 0 || height <= 0) return
                      setNaturalSizes((current) => {
                        const known = current.get(entry.url) ?? entry
                        if (known.width === width && known.height === height) return current
                        return new Map(current).set(entry.url, { width, height })
                      })
                    }}
                    onError={() => setBroken((current) => new Set(current).add(entry.key))}
                  />
                )}
              </span>
            </button>
          )
        })}
      </div>
      <div className={styles.counter}>
        {shown} / {total}
      </div>
      {showToggle && (
        <button type="button" className={styles.more} onClick={handleToggle}>
          {allShown ? '접기' : '더보기'}
        </button>
      )}
      {openIndex !== null && (
        <Lightbox entries={entries} initialIndex={openIndex} onClose={() => setOpenIndex(null)} />
      )}
    </div>
  )
}
